import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Menu from "../../Components/Common/Web/Menu";
import Cart from "../../Components/Common/Web/Cart";
import ChooseServingOption from "../../Components/Common/Web/ChooseServingOption";
import OrderTypeSelector from "../../Components/Common/Web/OrderTypeSelector";
import ConfirmModal from "../../Components/Common/ConfirmModal";
import { createPublicOrderPayment } from "../../helpers/backend_helper";
import { clearCart, setCartContext } from "../../slices/Web/cart/cartSlice";
import useGeolocation from "../../hooks/useGeolocation";
import { clearResolvedQr } from "../../slices/TableQrs/reducer";
import { resolveTableQr } from "../../slices/TableQrs/thunk";
import OrderSuccess from "../../Components/Common/Web/OrderSuccess";
import WebMenuLandingFooter from "../../Components/Common/Web/WebMenuLandingFooter";
import { generateIdempotencyKey } from "../../helpers/idempotency";

const MAX_GPS_ACCURACY_IN_METER = 50;
const DEFAULT_MAX_DISTANCE_TO_SHOP_IN_METER = 80;

const isChromeOnIOS = () => {
    if (typeof navigator === "undefined") {
        return false;
    }

    const userAgent = navigator.userAgent;
    const isIOS =
        /iPhone|iPad|iPod/i.test(userAgent) ||
        (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

    return isIOS && /CriOS/i.test(userAgent);
};

const calculateDistanceInMeter = (
    fromLatitude: number,
    fromLongitude: number,
    toLatitude: number,
    toLongitude: number,
) => {
    const earthRadius = 6371000;
    const toRad = (value: number) => (value * Math.PI) / 180;

    const latitudeDelta = toRad(toLatitude - fromLatitude);
    const longitudeDelta = toRad(toLongitude - fromLongitude);
    const a =
        Math.sin(latitudeDelta / 2) * Math.sin(latitudeDelta / 2) +
        Math.cos(toRad(fromLatitude)) *
        Math.cos(toRad(toLatitude)) *
        Math.sin(longitudeDelta / 2) *
        Math.sin(longitudeDelta / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return earthRadius * c;
};

const ShopMenu = () => {
    const { qrToken } = useParams();
    const dispatch: any = useDispatch();
    const requiresSafari = useMemo(() => isChromeOnIOS(), []);
    const cart = useSelector((state: any) => state.Cart);
    const shopMenuState = useSelector((state: any) => state.ShopMenu);
    const qrState = useSelector((state: any) => state.TableQrs);
    const table = qrState?.resolved?.table_id ? String(qrState.resolved.table_id) : null;
    const shopSlug = qrState?.resolved?.shop_slug ?? null;

    const shopMenu = shopMenuState?.shopMenu;
    const isShopMenuLoaded = shopMenuState?.success?.getShopMenu;
    const shopInfo = useMemo(() => shopMenu?.shop ?? shopMenu, [shopMenu]);
    const hasLoadedShopInfo = Boolean(isShopMenuLoaded && shopInfo);
    const shopTables = useMemo(() => (Array.isArray(shopInfo?.tables) ? shopInfo.tables : []), [shopInfo]);
    const maxDistanceToShopInMeter = useMemo(() => {
        const value = Number(shopInfo?.max_qr_distance_meters ?? shopInfo?.maxQrDistanceMeters);
        return Number.isFinite(value) && value > 0 ? value : DEFAULT_MAX_DISTANCE_TO_SHOP_IN_METER;
    }, [shopInfo?.maxQrDistanceMeters, shopInfo?.max_qr_distance_meters]);
    const publicToneColor = useMemo(() => {
        const resolvedQr = qrState?.resolved;
        const candidates = [
            resolvedQr?.tone_color,
            resolvedQr?.toneColor,
            resolvedQr?.shop?.tone_color,
            resolvedQr?.shop?.toneColor,
            shopInfo?.tone_color,
            shopInfo?.toneColor,
        ];

        return candidates.find((value) => typeof value === "string" && value.trim().length > 0) ?? "#2563EB";
    }, [qrState?.resolved, shopInfo?.toneColor, shopInfo?.tone_color]);

    useEffect(() => {
        if (typeof document === "undefined") {
            return;
        }

        document.documentElement.style.setProperty("--tone-color", publicToneColor);

        return () => {
            document.documentElement.style.setProperty("--tone-color", "#2563EB");
        };
    }, [publicToneColor]);

    useEffect(() => {
        if (requiresSafari) {
            dispatch(clearResolvedQr());
            return;
        }

        if (!qrToken) {
            dispatch(clearResolvedQr());
            return;
        }
        dispatch(resolveTableQr(qrToken));
        return () => {
            dispatch(clearResolvedQr());
        };
    }, [dispatch, qrToken, requiresSafari]);

    const {
        position: customerPosition,
        error: geolocationError,
    } = useGeolocation({
        watch: !requiresSafari,
        enableHighAccuracy: true,
        maximumAge: 0,
        timeout: 15000,
    });
    const [gpsError, setGpsError] = useState<string | null>(null);
    const [hasCopiedSafariLink, setHasCopiedSafariLink] = useState(false);

    const [currentPage, setCurrentPage] = useState('menu');
    const [orderType, setOrderType] = useState<string | null>(null);
    const [pendingServingOption, setPendingServingOption] = useState<string | null>(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [isSubmittingOrder, setIsSubmittingOrder] = useState(false);
    const [createdOrderId, setCreatedOrderId] = useState<string | null>(null);
    const [createdOrder, setCreatedOrder] = useState<any | null>(null);
    const orderIdempotencyKeyRef = useRef<string | null>(null);

    useEffect(() => {
        if (!shopInfo?.id || !qrToken) {
            return;
        }

        dispatch(setCartContext({
            shopId: shopInfo.id,
            qrToken,
        }));
    }, [dispatch, qrToken, shopInfo?.id]);

    const handleItemAdded = () => setCurrentPage('menu');
    const handleCartClick = () => setCurrentPage('cart');
    const handleBackToMenu = () => setCurrentPage('menu');
    const handleCheckout = () => setCurrentPage('orderType');
    const handleBackToCart = () => setCurrentPage('cart');
    const handleBackToOrderType = () => setCurrentPage('orderType');
    const handleBackToMenuFromSuccess = () => {
        setCurrentPage('menu');
        setCreatedOrderId(null);
        setCreatedOrder(null);
    };
    const handleOrderTypeSelect = (type: string) => {
        setOrderType(type);
        setCurrentPage('servingOption');
    };

    const handleCopySafariLink = async () => {
        const currentUrl = window.location.href;

        try {
            await navigator.clipboard.writeText(currentUrl);
            setHasCopiedSafariLink(true);
        } catch {
            window.prompt("Sao chép liên kết này và mở bằng Safari:", currentUrl);
        }
    };

    const handleServingOptionSelect = (option: string) => {
        if (!cart.items.length) {
            setSubmitError("Giỏ hàng trống. Vui lòng chọn món trước khi gửi đơn.");
            return;
        }

        if (gpsError) {
            setSubmitError(gpsError);
            return;
        }

        setPendingServingOption(option);
        setSubmitError(null);
        setShowConfirmModal(true);
    };

    const normalizeId = (value?: string | number | null) => {
        if (value === null || value === undefined) return null;
        const numeric = Number(value);
        return Number.isFinite(numeric) ? numeric : null;
    };

    const findTableMatch = useCallback((value: string) => {
        const normalizedInput = value.trim().toLowerCase();
        return shopTables.find((tableItem: any) => {
            const tableId = normalizeId(tableItem?.id);
            const idString = tableId ? tableId.toString() : null;
            const number = (tableItem?.number ?? tableItem?.name ?? '').toString().trim().toLowerCase();
            return (idString && idString === normalizedInput) || number === normalizedInput;
        });
    }, [shopTables]);

    const resolveReceivingPlace = (option: string) => (option && option !== 'counter' ? 2 : 1);

    const isQrResolving = qrState?.resolving;
    const qrResolveError = qrState?.resolveError;
    const hasRequiredTable = Boolean(table);
    const hasValidTable = useMemo(() => (table ? Boolean(findTableMatch(table)) : false), [findTableMatch, table]);
    const shouldShowInvalidTable = Boolean(table) && isShopMenuLoaded && !hasValidTable;

    useEffect(() => {
        const shopLatitude = Number(shopInfo?.latitude);
        const shopLongitude = Number(shopInfo?.longitude);

        if (!hasLoadedShopInfo) {
            setGpsError(null);
            return;
        }

        if (!Number.isFinite(shopLatitude) || !Number.isFinite(shopLongitude)) {
            setGpsError("Quán chưa cấu hình vị trí. Không thể đặt món.");
            return;
        }

        if (geolocationError) {
            setGpsError(geolocationError);
            return;
        }

        if (customerPosition.latitude === null || customerPosition.longitude === null) {
            setGpsError("Đang xác định vị trí của bạn...");
            return;
        }

        if (customerPosition.accuracy !== null && customerPosition.accuracy > MAX_GPS_ACCURACY_IN_METER) {
            setGpsError("GPS không chính xác, vui lòng bật định vị.");
            return;
        }

        const distance = calculateDistanceInMeter(
            customerPosition.latitude,
            customerPosition.longitude,
            shopLatitude,
            shopLongitude,
        );

        if (distance > maxDistanceToShopInMeter) {
            setGpsError(`Bạn cần đứng trong phạm vi ${maxDistanceToShopInMeter}m của quán để đặt món hoặc gọi phục vụ.`);
            return;
        }

        setGpsError(null);
    }, [
        customerPosition.accuracy,
        customerPosition.latitude,
        customerPosition.longitude,
        geolocationError,
        hasLoadedShopInfo,
        shopInfo?.latitude,
        maxDistanceToShopInMeter,
        shopInfo?.longitude,
    ]);

    const buildMenusPayload = () => cart.items.map((item: any) => ({
        id: normalizeId(item.id),
        quantity: item.quantity,
        options: item.option ?? {},
        note: item.note ?? undefined,
    }));

    const buildOrderPayload = (option: string) => {
        if (!shopInfo?.id) throw new Error("Không tìm thấy thông tin cửa hàng.");
        if (!cart.items.length) throw new Error("Giỏ hàng trống.");
        if (
            String(cart.shopId ?? "") !== String(shopInfo.id) ||
            String(cart.qrToken ?? "") !== String(qrToken ?? "")
        ) {
            throw new Error("Giỏ hàng không thuộc mã QR hiện tại. Vui lòng chọn món lại.");
        }
        if (!orderType) throw new Error("Vui lòng chọn hình thức nhận món.");

        const tableId = normalizeId(table);
        if (!tableId) throw new Error("Thiếu mã bàn hợp lệ.");

        return {
            shop_id: shopInfo.id,
            payment_method: 'cash',
            order_type: orderType,
            receiving_place: resolveReceivingPlace(option),
            table_id: tableId,
            callback_url: `${window.location.origin}/web-menu/${qrToken ?? ""}`,
            customer_latitude: customerPosition.latitude,
            customer_longitude: customerPosition.longitude,
            customer_accuracy: customerPosition.accuracy,
            menus: buildMenusPayload(),
        };
    };

    const handleSubmitOrder = async () => {
        if (!pendingServingOption || isSubmittingOrder) return;

        setIsSubmittingOrder(true);
        setSubmitError(null);

        try {
            const payload = buildOrderPayload(pendingServingOption);
            const idempotencyKey = orderIdempotencyKeyRef.current ?? generateIdempotencyKey();
            orderIdempotencyKeyRef.current = idempotencyKey;
            const response = await createPublicOrderPayment(payload, idempotencyKey);
            const normalizedResponse = response && typeof response === "object" && "data" in (response as any)
                ? (response as any).data
                : response;
            const orderId = normalizedResponse?.orderId ?? normalizedResponse?.order_id ?? normalizedResponse?.data?.orderId;
            const orderSummary = normalizedResponse?.order ?? normalizedResponse?.data?.order ?? normalizedResponse?.data;

            if (!orderId) throw new Error("Không xác định được mã đơn hàng sau khi gửi.");
            if (!orderSummary) throw new Error("Không nhận được thông tin đơn hàng sau khi gửi.");

            dispatch(clearCart());
            orderIdempotencyKeyRef.current = null;
            setShowConfirmModal(false);
            setCreatedOrderId(String(orderId));
            setCreatedOrder(orderSummary);
            setCurrentPage('orderSuccess');
        } catch (error: any) {
            const message = typeof error === 'string' ? error : error?.message ?? 'Không thể gửi đơn. Vui lòng thử lại.';
            setSubmitError(message);
        } finally {
            setIsSubmittingOrder(false);
        }
    };

    const renderPage = () => {
        switch (currentPage) {
            case 'menu':
                return <Menu onItemAdded={handleItemAdded} onCartClick={handleCartClick} shopSlug={shopSlug} table={table} customerPosition={customerPosition} gpsError={gpsError} />;
            case 'cart':
                return <Cart onCheckout={handleCheckout} onBack={handleBackToMenu} />;
            case 'orderType':
                return <OrderTypeSelector onSelect={handleOrderTypeSelect} onBack={handleBackToCart} />;
            case 'servingOption':
                return <ChooseServingOption onSelect={handleServingOptionSelect} table={table ?? undefined} shopSlug={shopSlug} onBack={handleBackToOrderType} />;
            case 'orderSuccess':
                return (
                    <OrderSuccess
                        orderState={{
                            order: createdOrder,
                            loading: { getOrder: false },
                            success: { getOrder: Boolean(createdOrder) },
                        }}
                        createdOrderId={createdOrderId}
                        onBackToMenu={handleBackToMenuFromSuccess}
                    />
                );
            default:
                return <Menu onItemAdded={handleItemAdded} shopSlug={shopSlug} table={table} customerPosition={customerPosition} gpsError={gpsError} />;
        }
    };

    if (requiresSafari) {
        return (
            <div className="d-flex flex-column align-items-center justify-content-center min-vh-100 text-center px-3 py-4">
                <div style={{ maxWidth: 460 }}>
                    <h1 className="h3 fw-bold mb-3">Vui lòng mở bằng Safari</h1>
                    <p className="text-muted mb-4">
                        Tính năng đặt món yêu cầu vị trí chính xác và theo dõi vị trí liên tục.
                        Chrome trên iPhone hiện không cấp quyền vị trí ổn định cho trang này.
                    </p>
                    <button
                        type="button"
                        className="btn btn-primary px-4"
                        onClick={handleCopySafariLink}
                    >
                        {hasCopiedSafariLink ? "Đã sao chép liên kết" : "Sao chép liên kết"}
                    </button>
                    <p className="small text-muted mt-3 mb-0">
                        Sau đó mở Safari, dán liên kết vào thanh địa chỉ và truy cập lại.
                    </p>
                </div>
            </div>
        );
    }

    if (isQrResolving) {
        return <div className="d-flex flex-column align-items-center justify-content-center min-vh-100 text-center px-3"><p className="text-muted mb-0">Đang tải...</p></div>;
    }

    const blockingErrorMessage = (qrResolveError || !hasRequiredTable || shouldShowInvalidTable) ? "Không tìm thấy trang." : gpsError || submitError;
    const blockingErrorTitle = (qrResolveError || !hasRequiredTable || shouldShowInvalidTable) ? "404" : "Đã xảy ra lỗi";

    if (blockingErrorMessage) {
        return (
            <div className="d-flex flex-column align-items-center justify-content-center min-vh-100 text-center px-3">
                <h1 className="display-5 fw-bold mb-3">{blockingErrorTitle}</h1>
                <p className="text-muted mb-0">{blockingErrorMessage}</p>
            </div>
        );
    }

    return (
        <React.Fragment>
            <ConfirmModal
                isOpen={showConfirmModal}
                typeConfirm="primary"
                title="Xác nhận gửi đơn"
                content={isSubmittingOrder ? "Đang gửi đơn..." : "Xác nhận gửi đơn đến quán?"}
                onConfirm={handleSubmitOrder}
                onCancel={() => {
                    orderIdempotencyKeyRef.current = null;
                    setShowConfirmModal(false);
                }}
                loading={isSubmittingOrder}
            />
            {renderPage()}
            <WebMenuLandingFooter />
        </React.Fragment>
    );
};

export default ShopMenu;
